import { useMemo } from 'react';
import { useParticipants, useTracks, VideoTrack, useLocalParticipant } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { useRoom } from '../room/RoomProvider';

const TALKING_AUDIO_LEVEL = 0.02;

export function StageParticipantGrid() {
  const liveKitParticipants = useParticipants();
  const tracks = useTracks([Track.Source.Camera], { onlySubscribed: false });
  const { localParticipant } = useLocalParticipant();
  const {
    hostParticipantIds,
    modelParticipantIds,
    participants: sessionParticipants,
  } = useRoom();
  const sessionParticipantById = useMemo(() => (
    new Map(sessionParticipants.map(participant => [participant.participantId, participant]))
  ), [sessionParticipants]);
  const modelIdSet = useMemo(() => (
    new Set([
      ...modelParticipantIds,
      ...sessionParticipants
        .filter(participant => participant.role === 'model')
        .map(participant => participant.participantId),
    ])
  ), [modelParticipantIds, sessionParticipants]);
  const sortedParticipants = useMemo(() => (
    [...liveKitParticipants].sort((a, b) => {
      const aIsModel = modelIdSet.has(a.identity);
      const bIsModel = modelIdSet.has(b.identity);
      if (aIsModel !== bIsModel) return aIsModel ? -1 : 1;

      const aIsHost = hostParticipantIds.includes(a.identity);
      const bIsHost = hostParticipantIds.includes(b.identity);
      if (aIsHost !== bIsHost) return aIsHost ? -1 : 1;

      return getDisplayName(a.identity, a.name, sessionParticipantById)
        .localeCompare(getDisplayName(b.identity, b.name, sessionParticipantById));
    })
  ), [hostParticipantIds, liveKitParticipants, modelIdSet, sessionParticipantById]);

  if (sortedParticipants.length === 0) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-[#0a0807] text-sm text-white/60">
        No participants yet
      </div>
    );
  }

  return (
    <div className="absolute inset-0 overflow-auto bg-[#0a0807] p-2 sm:p-3">
      <div
        className="grid min-h-full content-center gap-2 sm:gap-3"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))' }}
      >
        {sortedParticipants.map((participant) => {
          const track = tracks.find(trackRef =>
            trackRef.participant.identity === participant.identity &&
            trackRef.source === Track.Source.Camera
          );
          const displayName = getDisplayName(participant.identity, participant.name, sessionParticipantById);
          const isLocal = participant.identity === localParticipant?.identity;
          const isHost = hostParticipantIds.includes(participant.identity);
          const isModel = modelIdSet.has(participant.identity);
          const hasAudibleInput = participant.audioLevel >= TALKING_AUDIO_LEVEL;
          const tileClassName = [
            'relative aspect-video min-h-[96px] overflow-hidden rounded-md border bg-black shadow-sm transition-colors duration-300 ease-out',
            hasAudibleInput ? 'border-sky-400' : 'border-white/12',
          ].join(' ');

          return (
            <div
              key={participant.identity}
              className={tileClassName}
            >
              {track?.publication?.track ? (
                <VideoTrack
                  trackRef={track}
                  className="absolute inset-0 h-full w-full object-cover"
                  style={isLocal ? { transform: 'scaleX(-1)' } : undefined}
                />
              ) : (
                <div className="absolute inset-0 flex h-full w-full items-center justify-center bg-gradient-to-br from-[#2a1e15] to-[#0c0a09]">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full border border-white/12 bg-white/8 text-lg font-semibold text-white/70">
                    {displayName.trim().slice(0, 1).toUpperCase() || '?'}
                  </div>
                </div>
              )}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-black/10" />
              <div className="absolute bottom-2 left-2 right-2 flex items-end justify-between gap-2">
                <span className="min-w-0 truncate rounded bg-black/55 px-2 py-1 text-[11px] font-medium text-white backdrop-blur-sm">
                  {isLocal ? 'You' : displayName}
                </span>
                {(isHost || isModel) && (
                  <span className="flex-shrink-0 rounded bg-black/55 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-white/80 backdrop-blur-sm">
                    {isModel ? 'Model' : 'Host'}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function getDisplayName(
  participantId: string,
  liveKitName: string | undefined,
  sessionParticipantById: Map<string, { displayName?: string }>
) {
  return sessionParticipantById.get(participantId)?.displayName?.trim() || liveKitName || participantId.slice(0, 8);
}
